import { Colors } from "@jsq/ds/styles";
import { PaymentServicePaymentBatchStatus as PaymentBatchStatusEnum } from "../../graphql/supergraphEnums";
import { PaymentServicePaymentBatchStatus, PaymentMethod } from "../../graphql/supergraphTypes";
import { Payment } from "../PaymentsListWorkflow/PaymentBatchesTable/types";
import { paymentStatusUIConfig } from "../PaymentsListWorkflow/PaymentBatchesTable/util/paymentStatusUtils";
import { TextWithIconProps } from "../TextWithIcon";
import { batchStatusUIConfig, BatchStatusLabel } from "./StatusCell/utils";

/**
 * Type for combined status that can be either batch status or payment status summary
 */
export type CombinedStatusConfig = {
  label: string;
  labelColor: Colors;
  iconType?: TextWithIconProps["iconType"];
  iconColor?: Colors;
  iconVariant?: TextWithIconProps["iconVariant"];
  // For payment status summary (when transmitted)
  items?: TextWithIconProps[];
};

/**
 * Determines if a batch has been transmitted based on its status
 * Treats both Transmitted and TransmittingInProgress as the same status
 */
const isTransmitted = (status: PaymentServicePaymentBatchStatus): boolean => {
  return (
    status === PaymentBatchStatusEnum.Transmitted ||
    status === PaymentBatchStatusEnum.TransmittingInProgress
  );
};

/**
 * Provides the combined status to be displayed in the Status column
 *
 * Logic:
 * - Before batch is "Transmitted": Show batch status
 * - After batch is "Transmitted":
 *   - If any payment has a future scheduledProcessDate: Show "Scheduled"
 *   - Otherwise: Show payment status summary
 *
 * @param batchStatus - The current batch status
 * @param payments - Array of payments in the batch
 * @param isGPX - Whether this is a GPX client (affects ReadyForReview label)
 * @param method - The payment method (affects FundingInProgress and Funded labels)
 * @returns Combined status configuration
 */
export const getCombinedStatusConfig = (
  batchStatus: PaymentServicePaymentBatchStatus,
  payments: Payment[],
  isGPX?: boolean,
  method?: PaymentMethod,
): CombinedStatusConfig => {
  // If batch has been transmitted, check for scheduled or payment status
  if (isTransmitted(batchStatus)) {
    // Check if any payment's scheduled date is in the future
    const now = new Date();
    const hasScheduledInFuture = payments.some((payment) => {
      if (!payment.scheduledProcessDate) return false;
      const scheduledDate = new Date(payment.scheduledProcessDate);
      return scheduledDate > now;
    });

    // Show "Scheduled" if any payment has a future scheduled date
    if (hasScheduledInFuture) {
      return {
        label: BatchStatusLabel.Scheduled,
        labelColor: Colors.Neutral800,
        iconType: "circle-notch",
        iconColor: Colors.Neutral800,
      };
    }

    // Otherwise, show payment status summary
    const paymentStatusItems = paymentStatusUIConfig(payments);
    if (paymentStatusItems && paymentStatusItems.length > 0) {
      // For single item, we can show it as a simple status
      if (paymentStatusItems.length === 1) {
        const singleItem = paymentStatusItems[0];
        return {
          label: singleItem.label || "",
          labelColor: singleItem.labelColor || Colors.Neutral800,
          iconType: singleItem.iconType,
          iconColor: singleItem.iconColor,
          iconVariant: singleItem.iconVariant,
          items: paymentStatusItems,
        };
      }
      // For multiple items, we need to show them as a sequence
      return {
        label: "Multiple statuses",
        labelColor: Colors.Neutral800,
        items: paymentStatusItems,
      };
    }
  }

  const batchConfig = batchStatusUIConfig(batchStatus, isGPX, method);
  return {
    label: batchConfig.label,
    labelColor: batchConfig.labelColor,
    iconType: batchConfig.iconType,
    iconColor: batchConfig.iconColor,
    iconVariant: batchConfig.iconVariant,
  };
};
